import {Injectable, OnDestroy, signal} from '@angular/core';
import type {KaldiRecognizer, Model} from 'vosk-browser';

/** Lingue con un modello Vosk in assets/vosk (vedi scripts/download-vosk-models.sh). */
const LIVE_LANGUAGES = ['it', 'en', 'de', 'es'];

/**
 * Anteprima in diretta della domanda mentre si parla, riconosciuta nel browser con Vosk:
 * l'audio non lascia il dispositivo. E' solo un'anteprima: alla fine il testo viene sostituito
 * dalla trascrizione di Whisper, piu' precisa. Se il modello non si carica, la registrazione
 * funziona lo stesso, senza anteprima.
 * Da fornire nel componente (`providers`), come VoiceRecorderService.
 */
@Injectable()
export class LiveTranscriptionService implements OnDestroy {
  /** Testo riconosciuto finora (frasi concluse + frase in corso). */
  readonly text = signal('');
  /** Il modello della lingua si sta scaricando (solo al primo uso). */
  readonly loading = signal(false);

  /** Modelli gia' caricati, per lingua: si scaricano una volta per pagina. */
  private models = new Map<string, Promise<Model>>();
  private recognizer: KaldiRecognizer | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private finals: string[] = [];
  private partial = '';
  private session = 0;

  /** Avvia l'anteprima sul microfono gia' aperto dal registratore. Non lancia mai errori. */
  async start(stream: MediaStream, language: string): Promise<void> {
    this.stop();
    this.text.set('');
    const session = ++this.session;
    const lang = language.split('-')[0].toLowerCase();
    if (!LIVE_LANGUAGES.includes(lang)) {
      return;
    }
    try {
      this.loading.set(!this.models.has(lang));
      const model = await this.model(lang);
      this.loading.set(false);
      if (session !== this.session || !stream.active) {
        return;  // nel frattempo la registrazione e' finita
      }
      const Ctx = window.AudioContext ?? (window as any).webkitAudioContext;
      const context: AudioContext = new Ctx();
      void context.resume?.();
      const recognizer = new model.KaldiRecognizer(context.sampleRate);
      recognizer.on('result', (message: any) => {
        const text = (message?.result?.text ?? '').trim();
        if (text) {
          this.finals.push(text);
        }
        this.partial = '';
        this.publish();
      });
      recognizer.on('partialresult', (message: any) => {
        this.partial = (message?.result?.partial ?? '').trim();
        this.publish();
      });
      // ScriptProcessorNode e' deprecato ma e' l'unico disponibile ovunque, Safari compreso
      const processor = context.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (event) => {
        try {
          recognizer.acceptWaveform(event.inputBuffer);
        } catch {
          // anteprima persa per questo pezzo: la trascrizione finale non ne risente
        }
      };
      const source = context.createMediaStreamSource(stream);
      source.connect(processor);
      processor.connect(context.destination);
      this.audioContext = context;
      this.recognizer = recognizer;
      this.processor = processor;
      this.source = source;
    } catch {
      this.loading.set(false);
      this.models.delete(lang);  // al prossimo tentativo si riprova a caricarlo
    }
  }

  /** Ferma l'anteprima; restituisce l'ultimo testo riconosciuto (riserva se Whisper non risponde). */
  stop(): string {
    this.session++;
    const text = this.text();
    this.processor?.disconnect();
    this.source?.disconnect();
    if (this.processor) {
      this.processor.onaudioprocess = null;
    }
    try {
      this.recognizer?.remove();
    } catch {
      // gia' rimosso
    }
    void this.audioContext?.close().catch(() => undefined);
    this.processor = null;
    this.source = null;
    this.recognizer = null;
    this.audioContext = null;
    this.finals = [];
    this.partial = '';
    this.loading.set(false);
    return text;
  }

  /** Svuota il testo mostrato (dopo che la trascrizione finale e' arrivata). */
  clear(): void {
    this.text.set('');
  }

  ngOnDestroy(): void {
    this.stop();
    this.models.forEach((model) => model.then((m) => m.terminate()).catch(() => undefined));
  }

  private model(lang: string): Promise<Model> {
    let model = this.models.get(lang);
    if (!model) {
      // Libreria (~6 MB) caricata solo al primo uso del microfono
      model = import('vosk-browser').then((mod: any) => {
        // Pacchetto UMD/CommonJS: con l'import dinamico le funzioni stanno sotto `default`
        const vosk = mod?.createModel ? mod : (mod?.default ?? (window as any).Vosk);
        return vosk.createModel(new URL(`assets/vosk/${lang}.tar.gz`, document.baseURI).href, -1) as Promise<Model>;
      });
      this.models.set(lang, model);
    }
    return model;
  }

  private publish(): void {
    this.text.set([...this.finals, this.partial].filter(Boolean).join(' '));
  }
}
